import React from "react";
import {
    Box, Paper, Grid, TextField, Select, MenuItem, FormControl, InputLabel,
    FormHelperText, Typography, ToggleButton, ToggleButtonGroup, Autocomplete,
} from "@mui/material";
import {
    RACE_TYPES, SECTORS, RTYP_OPTIONS, FIS_CATEGORY_OPTIONS, GENDER_OPTIONS,
    FIELD_RULES, CATEGORY_KEY, disciplineOptions, deriveSeason, validateHeader,
} from "./raceTypes";

// Correspondance RTYP -> Category FIS (seules COC/FIS/WC ont un équivalent).
const RTYP_TO_FIS_CATEGORY = { COC: "COC", FIS: "FIS", WC: "WC" };

// ---------------------------------------------------------------------
// Écran affiché à la création d'une course (config sans raceType) :
// un clic sur un type initialise les champs et ouvre le formulaire.
// ---------------------------------------------------------------------
// Modifié le 01.10.2026 : le secteur (Alpin / Télémark) se choisit dès la
// création — il active en télémark les pénalités style/jump dans le
// classement chrono et les temps envoyés à la FIS.
// initialSector : secteur déjà choisi à la création de la course (hub :
// Alpin / Télémark, transmis par ?sector=) — 03.11.2026.
export function RaceTypeChooser({ onChoose, initialSector = "AL" }) {
    const [sector, setSector] = React.useState(initialSector === "TM" ? "TM" : "AL");
    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" mb={1}>Nouvelle course</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                Le secteur et le type déterminent les champs à saisir, le calcul des points, les pénalités
                et les exports disponibles. Ils restent modifiables ensuite.
            </Typography>
            <Typography variant="subtitle2" mb={1}>1. Secteur</Typography>
            <ToggleButtonGroup exclusive size="small" value={sector} onChange={(_, v) => v && setSector(v)} sx={{ mb: 2 }}>
                <ToggleButton value="AL">Alpin</ToggleButton>
                <ToggleButton value="TM">Télémark</ToggleButton>
                <ToggleButton value="OTHER">Autre</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="subtitle2" mb={1}>2. Type de course</Typography>
            <Grid container spacing={2}>
                {RACE_TYPES.map((t) => (
                    <Grid item xs={12} md={4} key={t.value}>
                        <Box
                            component="button"
                            type="button"
                            onClick={() => onChoose(t.value, sector)}
                            sx={{
                                width: "100%", textAlign: "left", p: 2, cursor: "pointer",
                                border: "1px solid", borderColor: "divider", borderRadius: 1,
                                bgcolor: "background.paper",
                                "&:hover, &:focus-visible": { borderColor: "primary.main", outline: "none" },
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight="bold">{t.label}</Typography>
                            <Typography variant="body2" color="text.secondary">{t.description}</Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </Paper>
    );
}

// ---------------------------------------------------------------------
// Formulaire Raceheader piloté par FIELD_RULES : les champs "hidden" ne
// sont pas rendus, "required" porte l'astérisque, les erreurs de
// validateHeader() s'affichent sous chaque champ.
// ---------------------------------------------------------------------
// Props optionnelles (ajoutées pour l'export FIS des courses parallèles,
// ParallelFisExport.jsx) :
//   hiddenFields      : champs à ne pas afficher (ex: ["sector"])
//   disciplineChoices : liste { value, label } qui remplace celle du secteur
//   hideTypeToggle    : masque le choix FIS / Swiss-Ski / Populaire
//   title             : titre du bloc (défaut "Raceheader")
export default function RaceHeaderForm({
                                           config, setConfig, onTypeChange, hiddenFields = [], disciplineChoices, hideTypeToggle = false, title = "Raceheader",
                                       }) {
    const rules = FIELD_RULES[config.raceType] || {};
    const { errors, warnings } = validateHeader(config);
    const show = (f) => rules[f] && rules[f] !== "hidden" && !hiddenFields.includes(f);
    const req = (f) => rules[f] === "required";
    const help = (f, fallback) => errors[f] || warnings[f] || fallback;

    const set = (field, value) => setConfig((prev) => ({ ...prev, [field]: value }));

    const setDate = (value) =>
        setConfig((prev) => ({
            ...prev,
            date: value,
            // La saison suit la date tant qu'elle n'a pas été corrigée à la main.
            season: prev.seasonManual ? prev.season : deriveSeason(value) || prev.season,
        }));

    const setSector = (value) =>
        setConfig((prev) => {
            const stillValid = disciplineOptions(prev.raceType, value).some((d) => d.value === prev.discipline);
            return { ...prev, sector: value, discipline: stillValid || value === "OTHER" ? prev.discipline : "" };
        });

    const setRtyp = (value) =>
        setConfig((prev) => ({
            ...prev,
            rtyp: value,
            fisCategory: prev.fisCategory || RTYP_TO_FIS_CATEGORY[value] || "",
        }));

    const codexHelp = {
        fis: config.codex && /^\d{1,3}$/.test(config.codex)
            ? `Exporté comme ${config.codex.padStart(4, "0")} dans les noms de fichiers`
            : "Numéro FIS pur (ex: 1508), sans préfixe nation",
        swissski: "Codex Swiss-Ski de la course",
    }[config.raceType];

    const categoryKey = CATEGORY_KEY[config.raceType];
    const discOptions = disciplineChoices || disciplineOptions(config.raceType, config.sector);

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                <Typography variant="h6">{title}</Typography>
                {!hideTypeToggle && <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={config.raceType}
                    onChange={(_, v) => v && onTypeChange(v)}
                >
                    {RACE_TYPES.map((t) => (
                        <ToggleButton key={t.value} value={t.value}>{t.label}</ToggleButton>
                    ))}
                </ToggleButtonGroup>}
            </Box>

            <Grid container spacing={2}>
                {show("sector") && (
                    <Grid item xs={12} sm={4}>
                        <FormControl size="small" fullWidth required={req("sector")} error={!!errors.sector}>
                            <InputLabel>Secteur</InputLabel>
                            <Select label="Secteur" value={config.sector || ""} onChange={(e) => setSector(e.target.value)}>
                                {SECTORS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                            </Select>
                            {errors.sector && <FormHelperText>{errors.sector}</FormHelperText>}
                        </FormControl>
                    </Grid>
                )}

                {show("discipline") && (
                    <Grid item xs={12} sm={4}>
                        {config.sector === "OTHER" || discOptions.length === 0 ? (
                            <TextField
                                label="Discipline" size="small" fullWidth required={req("discipline")}
                                value={config.discipline || ""}
                                onChange={(e) => set("discipline", e.target.value)}
                                error={!!errors.discipline} helperText={help("discipline", "Saisie libre")}
                            />
                        ) : (
                            <FormControl size="small" fullWidth required={req("discipline")} error={!!errors.discipline}>
                                <InputLabel>Discipline</InputLabel>
                                <Select label="Discipline" value={config.discipline || ""} onChange={(e) => set("discipline", e.target.value)}>
                                    {discOptions.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                                </Select>
                                <FormHelperText>{help("discipline", " ")}</FormHelperText>
                            </FormControl>
                        )}
                    </Grid>
                )}

                {show("gender") && (
                    <Grid item xs={12} sm={4}>
                        <FormControl size="small" fullWidth required={req("gender")} error={!!errors.gender}>
                            <InputLabel>Genre</InputLabel>
                            <Select label="Genre" value={config.gender || ""} onChange={(e) => set("gender", e.target.value)}>
                                {!req("gender") && <MenuItem value="">—</MenuItem>}
                                {GENDER_OPTIONS.map((g) => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
                            </Select>
                            <FormHelperText>{help("gender", " ")}</FormHelperText>
                        </FormControl>
                    </Grid>
                )}

                {show("codex") && (
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Codex" size="small" fullWidth required={req("codex")}
                            value={config.codex || ""}
                            onChange={(e) => set("codex", e.target.value.trim())}
                            error={!!errors.codex} helperText={help("codex", codexHelp)}
                        />
                    </Grid>
                )}

                {show("date") && (
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Date (JJ.MM.AAAA)" size="small" fullWidth required={req("date")}
                            placeholder="19.12.2026"
                            value={config.date || ""}
                            onChange={(e) => setDate(e.target.value)}
                            error={!!errors.date} helperText={help("date", " ")}
                        />
                    </Grid>
                )}

                {show("season") && (
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Saison" size="small" fullWidth required={req("season")}
                            value={config.season || ""}
                            onChange={(e) => setConfig((p) => ({ ...p, season: e.target.value, seasonManual: e.target.value !== "" }))}
                            error={!!errors.season}
                            helperText={errors.season || (config.seasonManual ? "Saisie manuelle (vider pour recalculer)" : "Calculée depuis la date")}
                        />
                    </Grid>
                )}

                {show("event") && (
                    <Grid item xs={12} sm={8}>
                        <TextField
                            label={config.raceType === "fis" ? "Event (nom au calendrier FIS)" : "Event"}
                            size="small" fullWidth required={req("event")}
                            value={config.event || ""}
                            onChange={(e) => set("event", e.target.value)}
                            error={!!errors.event} helperText={help("event", " ")}
                        />
                    </Grid>
                )}

                {show("place") && (
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Place" size="small" fullWidth required={req("place")}
                            value={config.place || ""}
                            onChange={(e) => set("place", e.target.value)}
                            error={!!errors.place} helperText={help("place", " ")}
                        />
                    </Grid>
                )}

                {show("nation") && (
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Nation organisatrice" size="small" fullWidth required={req("nation")}
                            value={config.nation || ""}
                            inputProps={{ maxLength: 3 }}
                            onChange={(e) => set("nation", e.target.value.toUpperCase())}
                            error={!!errors.nation} helperText={help("nation", "Code FIS à 3 lettres")}
                        />
                    </Grid>
                )}

                {show("category") && config.raceType === "swissski" && (
                    <Grid item xs={12} sm={4}>
                        <FormControl size="small" fullWidth required={req("category")} error={!!errors.category}>
                            <InputLabel>RTyp</InputLabel>
                            <Select label="RTyp" value={config.rtyp || ""} onChange={(e) => setRtyp(e.target.value)}>
                                {RTYP_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                            </Select>
                            <FormHelperText>{help("category", "Classification Swiss-Ski")}</FormHelperText>
                        </FormControl>
                    </Grid>
                )}

                {show("category") && config.raceType === "fis" && (
                    <Grid item xs={12} sm={4}>
                        <Autocomplete
                            freeSolo
                            options={FIS_CATEGORY_OPTIONS}
                            value={config.fisCategory || null}
                            inputValue={config.fisCategory || ""}
                            onChange={(_, v) => set("fisCategory", (v || "").toUpperCase())}
                            onInputChange={(_, v) => set("fisCategory", (v || "").toUpperCase())}
                            renderInput={(params) => (
                                <TextField
                                    {...params} label="Catégorie FIS" size="small" required={req("category")}
                                    error={!!errors.category} helperText={help("category", "Selon le calendrier FIS")}
                                />
                            )}
                        />
                    </Grid>
                )}

                {show("category") && config.raceType === "populaire" && (
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Catégorie de course" size="small" fullWidth
                            value={config[categoryKey] || ""}
                            onChange={(e) => set(categoryKey, e.target.value)}
                            helperText="Libre (ex: Course de club)"
                        />
                    </Grid>
                )}

                <Grid item xs={12} sm={4}>
                    <TextField
                        label="Chronométrage" size="small" fullWidth
                        placeholder="Alge TDC-8000"
                        value={config.timing || ""}
                        onChange={(e) => set("timing", e.target.value)}
                    />
                </Grid>
            </Grid>
        </Paper>
    );
}